import dayjs from "$routes/_lib/dayjs";
import { validationMessages } from "$routes/_lib/validation/messages";

const required = (message) => (value) =>
  !value || value === "" ? message : undefined;

export const fieldRequired = required(validationMessages.mixed.required);

const notEmpty = (message) => (value) => (value === "" ? message : undefined);
export const fieldNotEmpty = notEmpty(validationMessages.mixed.required);

const validDate = (message) => (value) =>
  !value ||
  (new Date(value).toString() !== "Invalid Date" &&
    !Number.isNaN(new Date(value)))
    ? undefined
    : message;
export const fieldValidDate = validDate(validationMessages.date.valid);

export const validDOB = (message) => (value) => {
  const firstValidDate = dayjs().subtract(110, "year");
  const now = dayjs();
  const date = dayjs(value);
  return value &&
    (!date.isValid() || date.isBefore(firstValidDate) || now.isBefore(date))
    ? message
    : undefined;
};
export const fieldValidDOB = validDOB(validationMessages.date.dob);

export const validateIf = (fieldName, requiredCheck) => (value, values) => {
  if (values?.[fieldName]) {
    return requiredCheck(value);
  }
  return undefined;
};

export const minLength = (min) => {
  const msg = { ...validationMessages.string.min, values: { min } };
  return (value) => (value && value.length < min ? msg : undefined);
};

export const maxLength = (max) => {
  const msg = { ...validationMessages.string.max, values: { max } };
  return (value) => (value && value.length > max ? msg : undefined);
};

export const fixedLength = (len) => {
  const msg = { ...validationMessages.string.length, values: { length: len } };
  return (value) => (value && value.length !== len ? msg : undefined);
};

export const specialCharRegex =
  /^(?=.*[*" !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~"*]).*[a-zA-Z\d*" !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~"*]$/;
export const singleNumberMinRegex =
  /^(?=.*\d).*[a-zA-Z\d*" !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~"*]$/;
export const lowercaseCharRegex =
  /^(?=.*[a-z]).*[a-zA-Z\d*" !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~"*]$/;

export const password = (value) => {
  if (!value) {
    return undefined;
  }
  const min = 6;
  if (value.length < min) {
    return { ...validationMessages.string.min, values: { min } };
  }
  if (!specialCharRegex.test(value)) {
    return validationMessages.string.specialChar;
  }
  if (!singleNumberMinRegex.test(value)) {
    return validationMessages.string.singleNumber;
  }
  if (!lowercaseCharRegex.test(value)) {
    return validationMessages.string.lowercaseChar;
  }
  return undefined;
};

export const email = (value) =>
  value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,11}$/i.test(value)
    ? validationMessages.string.email
    : undefined;

export const greaterThanZero = (value) =>
  value && value > 0 ? undefined : validationMessages.number.greaterThanZero;
export const positiveValue = (value) =>
  (value && value > 0) || value === 0 || value === "0"
    ? undefined
    : validationMessages.number.positive;

const recursiveValidation = (checkersObject, values, obj = {}) => {
  const aux = obj;

  const result = Object.keys(checkersObject).reduce((_acc, name) => {
    const checkers = checkersObject[name];
    const current = values?.[name];

    if (Object.prototype.toString.call(checkers) === "[object Object]") {
      aux[name] = recursiveValidation(checkers, current, aux[name]);
      if (values) return aux;
      return {};
    }

    if ({}.toString.call(checkers[0]) === "[object Function]") {
      const validateValue = checkers.find((checker) =>
        checker(current, values),
      );
      if (validateValue) aux[name] = validateValue(current, values);
      return aux;
    }

    if (current) {
      aux[name] = current.map((row) =>
        recursiveValidation(checkers[0], row, {}),
      );
      return aux;
    }

    return {};
  }, {});
  return result;
};

export const validateCreator = (checkersObject) => (values) =>
  recursiveValidation(checkersObject, values);
